import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, Terminal, ArrowRight, Loader2, X, Copy, Check } from "lucide-react";
import { getSymptomsFull, matchSymptom, formatApiErrorDetail } from "../lib/api";

const norm = (t) => (t || "").toString().toLowerCase();
const esc = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Deep search: word-start matching, exact phrase + command text weighted highest, AND-first.
function searchIssues(issues, q) {
  const phrase = norm(q).trim().replace(/\s+/g, " ");
  const res = phrase.split(" ").filter(Boolean).map((t) => new RegExp(`(^|[^a-z0-9])${esc(t)}`));
  if (!res.length) return [];
  const count = (txt) => res.filter((r) => r.test(txt)).length;
  const hits = [];
  for (const s of issues) {
    let score = 0;
    let best = null;
    let cmdSum = 0;
    const found = new Set();
    const mark = (txt) => res.forEach((r, i) => r.test(txt) && found.add(i));
    const title = norm(s.title);
    const head = norm(`${s.category} ${s.summary}`);
    mark(title); mark(head);
    score += count(title) * 6 + count(head) * 2 + (title.includes(phrase) ? 20 : 0);
    for (const st of s.steps || []) {
      const stepText = norm(`${st.title} ${st.description} ${st.good_output} ${st.bad_signs}`);
      mark(stepText);
      score += count(stepText);
      for (const c of st.cli_commands || []) {
        const cmd = norm(c.command);
        const note = norm(`${c.note} ${c.platform}`);
        mark(cmd); mark(note);
        const cs = count(cmd) * 4 + count(note) + (cmd.includes(phrase) ? 25 : 0);
        if (cs && (!best || best.type !== "cmd" || cs > best.cs)) best = { type: "cmd", cs, step: st, cmd: c };
        cmdSum += cs;
      }
      if (!best && count(stepText)) best = { type: "step", step: st };
    }
    const top = best?.type === "cmd" ? best.cs : 0;
    score += top + (cmdSum - top) * 0.2;
    if (score) hits.push({ s, score, best, all: found.size === res.length });
  }
  const strict = hits.filter((h) => h.all);
  return (strict.length ? strict : hits).sort((a, b) => b.score - a.score).slice(0, 8);
}

const CopyCmd = ({ text, id }) => {
  const [ok, setOk] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setOk(true);
    setTimeout(() => setOk(false), 1500);
  };
  return (
    <button type="button" onClick={copy} data-testid={`finder-copy-${id}`} title="Copy command"
      className="ml-auto shrink-0 flex items-center gap-1 text-[11px] font-sans text-zinc-400 hover:text-white transition-colors">
      {ok ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
      {ok ? "Copied" : "Copy"}
    </button>
  );
};

const ResultRow = ({ hit, onGo }) => (
  <div role="button" tabIndex={0} data-testid={`finder-result-${hit.s.id}`} onClick={() => onGo(hit.s.id, hit.best?.step?.id)}
    onKeyDown={(e) => e.key === "Enter" && onGo(hit.s.id, hit.best?.step?.id)}
    className="group w-full text-left px-4 py-3 border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50 transition-colors cursor-pointer">
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[14px] font-semibold text-white truncate">{hit.s.title}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{hit.s.category}{hit.best?.step ? ` · ${hit.best.step.title}` : ""}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-blue-400 shrink-0" />
    </div>
    {hit.best?.type === "cmd" && (
      <div className="mt-2 flex items-start gap-2 font-mono text-[12px] text-emerald-300 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
        <Terminal className="h-3.5 w-3.5 mt-0.5 shrink-0 text-zinc-500" />
        <span className="break-all">{hit.best.cmd.command}</span>
        <CopyCmd text={hit.best.cmd.command} id={hit.s.id} />
      </div>
    )}
  </div>
);

const AiResults = ({ result, onGo }) => (
  <div data-testid="finder-ai-results" className="mt-4 space-y-2">
    {result.matches.map((m, i) => (
      <button key={m.id} data-testid={`finder-ai-match-${m.id}`} onClick={() => onGo(m.id)}
        className="group w-full text-left rounded-lg border border-zinc-800 bg-zinc-950 hover:border-blue-600/50 p-3 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-blue-400 bg-blue-600/15 rounded px-1.5 py-0.5">#{i + 1}</span>
          <span className="text-[14px] font-semibold text-white">{m.title}</span>
          <ArrowRight className="h-4 w-4 ml-auto text-zinc-600 group-hover:text-blue-400" />
        </div>
        {m.why && <div className="text-[13px] text-zinc-400 mt-1">{m.why}</div>}
      </button>
    ))}
    {result.general_guidance && (
      <div data-testid="finder-ai-guidance" className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-[13px] text-zinc-300 whitespace-pre-wrap">
        {result.general_guidance}
      </div>
    )}
    {!result.matches.length && !result.general_guidance && (
      <div className="text-[13px] text-zinc-500">No match found — try adding more detail.</div>
    )}
  </div>
);

export const IssueFinder = () => {
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { getSymptomsFull().then(setIssues).catch(() => {}); }, []);
  const hits = useMemo(() => searchIssues(issues, q), [issues, q]);
  const go = (id, stepId) => navigate(`/wizard/${id}${stepId ? `?step=${stepId}` : ""}`);

  const askAi = async () => {
    if (!q.trim()) return;
    setBusy(true); setErr(""); setAi(null);
    try { setAi(await matchSymptom(q.trim())); }
    catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail) || "AI finder failed — try again."); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="issue-finder" className="rounded-xl border border-blue-600/30 bg-zinc-900 p-5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
        <Sparkles className="h-4 w-4" /> Tell me what you're experiencing
      </div>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="finder-input" value={q} onChange={(e) => { setQ(e.target.value); setAi(null); }}
            onKeyDown={(e) => e.key === "Enter" && !hits.length && askAi()}
            placeholder='Search issues, fixes & commands — "one-way audio", "no inspect sip", "option 150"…'
            className="w-full h-11 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-blue-600 outline-none pl-9 pr-9 text-[14px] text-white placeholder:text-zinc-600" />
          {q && <button data-testid="finder-clear" onClick={() => { setQ(""); setAi(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>}
        </div>
        <button data-testid="finder-ask-ai" onClick={askAi} disabled={busy || !q.trim()}
          className="h-11 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-[13px] font-semibold flex items-center justify-center gap-2 transition-colors">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Ask AI to find it
        </button>
      </div>
      {q && !ai && (
        <div data-testid="finder-results" className="mt-3 rounded-lg border border-zinc-800 bg-zinc-950/50 max-h-96 overflow-y-auto">
          {hits.length ? hits.map((h) => <ResultRow key={h.s.id} hit={h} onGo={go} />) : (
            <div data-testid="finder-no-results" className="px-4 py-3 text-[13px] text-zinc-500">No exact match in the library — describe it in plain English and hit <span className="text-blue-400">Ask AI to find it</span>.</div>
          )}
        </div>
      )}
      {err && <div data-testid="finder-error" className="mt-3 text-[13px] text-red-400">{err}</div>}
      {ai && <AiResults result={ai} onGo={go} />}
    </section>
  );
};
